import React, { useEffect, useMemo, useState } from "react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import apiClient from "services/apiClient";

interface ChoicesByUrl {
  url: string;
  valueName?: string;
  titleName?: string;
}

interface MatrixDropdownProps {
  choices?: any[];
  choicesByUrl?: ChoicesByUrl | null;
  disabled?: boolean;
  editMode?: boolean;
  handleChange: (value: any) => void;
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  showLabel?: boolean;
  value?: any;
}

interface MatrixChoice {
  text: string;
  value: any;
}

const normalizeChoice = (
  choice: any,
  titleName = "text",
  valueName = "value",
): MatrixChoice => {
  if (choice !== null && typeof choice === "object") {
    const choiceValue = choice[valueName] ?? choice.value ?? choice.text;
    const choiceText =
      choice[titleName] ?? choice.text ?? String(choiceValue ?? "");

    return {
      text: String(choiceText),
      value: choiceValue,
    };
  }

  return {
    text: String(choice ?? ""),
    value: choice,
  };
};

const loadRemoteChoices = (source: ChoicesByUrl) => {
  const titleName = source.titleName || "text";
  const valueName = source.valueName || "value";

  return apiClient
    .get(source.url)
    .then(({ data }) =>
      (Array.isArray(data) ? data : []).map((choice) =>
        normalizeChoice(choice, titleName, valueName),
      ),
    )
    .catch(() => []);
};

const MatrixDropdown: React.FC<MatrixDropdownProps> = ({
  choices = [],
  choicesByUrl = null,
  disabled = false,
  editMode = true,
  handleChange,
  label,
  name,
  placeholder = "Select",
  required = false,
  showLabel = false,
  value,
}) => {
  const [remoteChoices, setRemoteChoices] = useState<MatrixChoice[]>([]);
  const valueName = choicesByUrl?.valueName || "value";
  const titleName = choicesByUrl?.titleName || "text";

  useEffect(() => {
    let isActive = true;

    if (choicesByUrl?.url) {
      loadRemoteChoices(choicesByUrl).then((loadedChoices) => {
        if (isActive) setRemoteChoices(loadedChoices);
      });
    } else {
      setRemoteChoices([]);
    }

    return () => {
      isActive = false;
    };
  }, [choicesByUrl?.titleName, choicesByUrl?.url, choicesByUrl?.valueName]);

  const options = useMemo(() => {
    if (choicesByUrl?.url) {
      return remoteChoices;
    }

    return choices.map((choice) => normalizeChoice(choice));
  }, [choices, choicesByUrl?.url, remoteChoices]);

  const selectedValue =
    value !== null && typeof value === "object"
      ? value.value ?? value[valueName]
      : value;
  const selectedChoice = options.find(
    (choice) => choice.value === selectedValue,
  );
  const hasValue =
    selectedValue !== null &&
    selectedValue !== undefined &&
    selectedValue !== "";

  const renderLabel = () => {
    if (!showLabel) return null;

    return (
      <label
        className="mb-1 text-xs font-semibold text-textNormal"
        htmlFor={name}
      >
        {label}
        {required ? " *" : ""}
      </label>
    );
  };

  if (!editMode) {
    const selectedText =
      selectedChoice?.text || normalizeChoice(value, titleName, valueName).text;

    return (
      <div className="flex min-h-12 flex-col justify-center py-2">
        {renderLabel()}
        <span
          className={
            hasValue
              ? "text-[15px] text-textAnswer dark:text-textMain"
              : "text-[15px] italic text-textNoSelection"
          }
        >
          {hasValue ? selectedText : "(No Selection)"}
        </span>
      </div>
    );
  }

  const selectChoice = (nextValue: any) => {
    const nextChoice = options.find((choice) => choice.value === nextValue);
    if (nextChoice) {
      handleChange(nextChoice);
    }
  };

  return (
    <div className="flex flex-col py-2">
      {renderLabel()}
      <FormControl fullWidth>
        <Select
          className="h-9 bg-bgWhite !font-Overpass"
          disabled={disabled}
          displayEmpty
          id={name}
          inputProps={{ "aria-label": label }}
          name={name}
          onChange={(event) => selectChoice(event.target.value)}
          required={required}
          value={hasValue ? selectedValue : ""}
        >
          <MenuItem disabled value="">
            {placeholder}
          </MenuItem>
          {options.map((choice, index) => (
            <MenuItem
              key={`${String(choice.value)}-${index}`}
              value={choice.value}
            >
              {choice.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

export default MatrixDropdown;
